import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  FiFolder, 
  FiFile, 
  FiCode, 
  FiCalendar,
  FiClock,
  FiDownload,
  FiUpload,
  FiTrash2,
  FiShare2,
  FiSearch,
  FiPlus,
  FiBarChart2
} from 'react-icons/fi'
import { IoMdRocket, IoMdFlash } from 'react-icons/io'
import { FaFire, FaDatabase } from 'react-icons/fa'
import { localStorageUtils } from '../hooks/useLocalStorage'
import toast, { Toaster } from 'react-hot-toast'

const Dashboard = () => {
  const [files, setFiles] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [isDark, setIsDark] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState([])

  useEffect(() => {
    const isDarkMode = localStorage.getItem('theme') === 'dark' || 
      window.matchMedia('(prefers-color-scheme: dark)').matches
    setIsDark(isDarkMode)
    loadFiles()
  }, [])

  const loadFiles = () => {
    try {
      const storedFiles = localStorageUtils.getItem('codeFiles', {})
      const filesArray = Object.values(storedFiles)
      setFiles(filesArray)
    } catch (error) {
      console.error('Error loading files:', error)
      setFiles([])
    }
  }

  const handleDelete = (fileId) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      const storedFiles = localStorageUtils.getItem('codeFiles', {})
      delete storedFiles[fileId]
      localStorageUtils.setItem('codeFiles', storedFiles)
      loadFiles()
      toast.success('File deleted successfully! 🗑️')
    }
  }

  const handleDeleteSelected = () => {
    if (selectedFiles.length === 0) {
      toast.error('No files selected')
      return
    }
    
    if (window.confirm(`Delete ${selectedFiles.length} selected files?`)) {
      const storedFiles = localStorageUtils.getItem('codeFiles', {})
      selectedFiles.forEach(id => {
        delete storedFiles[id]
      })
      localStorageUtils.setItem('codeFiles', storedFiles)
      setSelectedFiles([])
      loadFiles()
      toast.success(`${selectedFiles.length} files deleted! 🗑️`)
    }
  }

  const handleShare = (fileId) => {
    const url = `${window.location.origin}/editor/${fileId}`
    navigator.clipboard.writeText(url)
    toast.success('Link copied to clipboard! 🔗')
  }

  const handleImport = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = (e) => {
      const file = e.target.files[0]
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const importedData = JSON.parse(e.target.result)
          const storedFiles = localStorageUtils.getItem('codeFiles', {})
          
          Object.keys(importedData).forEach(key => {
            storedFiles[key] = importedData[key]
          })
          
          localStorageUtils.setItem('codeFiles', storedFiles)
          loadFiles()
          toast.success('Files imported successfully! 📤')
        } catch (error) {
          toast.error('Invalid file format')
        }
      }
      reader.readAsText(file)
    }
    input.click()
  }

  const handleExportAll = () => {
    const storedFiles = localStorageUtils.getItem('codeFiles', {})
    const dataStr = JSON.stringify(storedFiles, null, 2)
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr)
    
    const linkElement = document.createElement('a')
    linkElement.setAttribute('href', dataUri)
    linkElement.setAttribute('download', 'codefire-files.json')
    linkElement.click()
    
    toast.success('All files exported! 📥')
  }

  const toggleSelectFile = (fileId) => {
    setSelectedFiles(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    )
  }

  const selectAllFiles = () => {
    if (selectedFiles.length === filteredFiles.length) {
      setSelectedFiles([])
    } else {
      setSelectedFiles(filteredFiles.map(f => f.id))
    }
  }

  const filteredFiles = files.filter(file => 
    file.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    file.content?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const getStats = () => {
    const totalLines = files.reduce((sum, file) => sum + (file.content?.split('\n').length || 0), 0)
    const totalSize = files.reduce((sum, file) => sum + (file.content?.length || 0), 0)
    const languages = [...new Set(files.map(f => f.language))].filter(Boolean)
    
    return {
      totalFiles: files.length,
      totalLines,
      totalSize: (totalSize / 1024).toFixed(2),
      languages: languages.length,
      avgLines: files.length > 0 ? Math.round(totalLines / files.length) : 0
    }
  }

  const stats = getStats()

  return (
    <div className="min-h-screen pt-20 pb-8 bg-gradient-to-br from-primary-50 via-white to-red-50 dark:from-gray-900 dark:via-gray-800 dark:to-primary-900/30 transition-colors duration-300">
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: isDark ? '#1f2937' : '#ffffff',
            color: isDark ? '#ffffff' : '#1f2937',
            border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
            borderRadius: '12px',
            fontSize: '14px',
          },
        }}
      />
      
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                <span className="bg-gradient-to-r from-primary-600 to-red-500 bg-clip-text text-transparent">
                  Dashboard
                </span>
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage your CodeFire projects and files
              </p>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <Link to="/editor">
                <button className="flex items-center space-x-2 px-4 py-3 rounded-xl bg-gradient-to-r from-primary-600 to-red-500 text-white hover:from-primary-700 hover:to-red-600 shadow-lg">
                  <FiPlus />
                  <span>New Project</span>
                </button>
              </Link>
              <button
                onClick={handleImport}
                className="flex items-center space-x-2 px-4 py-3 rounded-xl bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <FiUpload />
                <span>Import</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {[
            { 
              label: 'Total Files', 
              value: stats.totalFiles, 
              icon: <FaDatabase />, 
              color: 'from-blue-500 to-cyan-500' 
            },
            { 
              label: 'Total Lines', 
              value: stats.totalLines.toLocaleString(), 
              icon: <FiCode />, 
              color: 'from-green-500 to-emerald-500' 
            },
            { 
              label: 'Storage Used', 
              value: `${stats.totalSize} KB`, 
              icon: <FiBarChart2 />, 
              color: 'from-purple-500 to-pink-500' 
            },
            { 
              label: 'Languages', 
              value: stats.languages, 
              icon: <IoMdFlash />, 
              color: 'from-orange-500 to-red-500' 
            },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                  <div className="text-white text-xl">
                    {stat.icon}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {stat.label}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Search and Actions */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 mb-8 shadow-lg"
        >
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1">
              <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search files..."
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 outline-none transition-all text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={selectAllFiles}
                className="px-4 py-3 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                {selectedFiles.length === filteredFiles.length ? 'Deselect All' : 'Select All'}
              </button>
              {selectedFiles.length > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  className="flex items-center space-x-2 px-4 py-3 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-600 dark:text-red-400"
                >
                  <FiTrash2 />
                  <span>Delete ({selectedFiles.length})</span>
                </button>
              )}
              <button
                onClick={handleExportAll}
                className="flex items-center space-x-2 px-4 py-3 rounded-xl bg-primary-500/10 hover:bg-primary-500/20 text-primary-600 dark:text-primary-400"
              >
                <FiDownload />
                <span>Export All</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Files Grid */}
        {filteredFiles.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16 bg-white dark:bg-gray-800 rounded-2xl shadow-lg"
          >
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-primary-100 to-red-100 dark:from-gray-700 dark:to-primary-900/20 flex items-center justify-center">
              <FaFire className="text-4xl text-primary-600 dark:text-primary-400 animate-pulse" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              No files found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              {searchQuery 
                ? `No files match "${searchQuery}". Try a different search term.`
                : 'Create your first code file to get started!'}
            </p>
            <Link to="/editor">
              <button className="px-6 py-3 rounded-xl bg-gradient-to-r from-primary-600 to-red-500 text-white hover:from-primary-700 hover:to-red-600 shadow-lg">
                Create First File
              </button>
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-6">
            {/* Files Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredFiles.map((file) => (
                <motion.div
                  key={file.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ y: -5 }}
                  className="group bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={selectedFiles.includes(file.id)}
                          onChange={() => toggleSelectFile(file.id)}
                          className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      </div>
                      <div>
                        <Link to={`/editor/${file.id}`}>
                          <h3 className="font-bold text-gray-900 dark:text-white truncate hover:text-primary-600 dark:hover:text-primary-400">
                            {file.name}
                          </h3>
                        </Link>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-xs px-2 py-1 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200">
                            {file.language || 'text'}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {file.content?.split('\n').length || 0} lines
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleShare(file.id)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        title="Share"
                      >
                        <FiShare2 className="text-green-500" />
                      </button>
                      <button
                        onClick={() => handleDelete(file.id)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        title="Delete"
                      >
                        <FiTrash2 className="text-red-500" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                    {file.content?.substring(0, 100) || 'No content'}...
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-500">
                    <div className="flex items-center space-x-1">
                      <FiCalendar />
                      <span>{new Date(file.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <FiClock />
                      <span>{new Date(file.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Dashboard